import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";

const RenewProductModal = ({ isOpen, product, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const todayStr = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState("");
  const [reminderDays, setReminderDays] = useState(15);
  const [totalCredits, setTotalCredits] = useState("");
  const [lowBalanceAlertAt, setLowBalanceAlertAt] = useState(
    product?.latest_renewal?.low_balance_alert_at || 500
  );
  const [amountPaid, setAmountPaid] = useState("");
  const [notes, setNotes] = useState("");

  if (!isOpen || !product) return null;

  const nextCycleNumber = (product.total_renewals || product.renewals?.length || 0) + 1;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate) {
      showToast.error("Start date is required");
      return;
    }
    if (product.type === "subscription" && !endDate) {
      showToast.error("End date is required for subscription renewal");
      return;
    }
    if (product.type === "credits" && (!totalCredits || Number(totalCredits) <= 0)) {
      showToast.error("Credits amount to add is required");
      return;
    }
    if (amountPaid === "" || Number(amountPaid) < 0) {
      showToast.error("Amount paid is required");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        start_date: startDate,
        amount_paid: Number(amountPaid),
        notes: notes.trim() || undefined,
      };

      if (product.type === "subscription") {
        payload.end_date = endDate;
        payload.reminder_before_days = Number(reminderDays) || 15;
      } else {
        payload.total_credits = Number(totalCredits);
        payload.low_balance_alert_at = lowBalanceAlertAt ? Number(lowBalanceAlertAt) : 0;
      }

      const response = await axios.post(
        `${API_URL}settings/product-subscriptions/${product.id}/renew`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.data) {
        showToast.success(`Cycle #${nextCycleNumber} recorded successfully!`);
        onSuccess(response.data.data);
        onClose();
      }
    } catch (err) {
      console.error("Error renewing product:", err);
      const msg = err.response?.data?.message || "Failed to record renewal.";
      showToast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050, overflowY: "auto" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-md modal-dialog-centered"
        style={{ margin: "1.75rem auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content with-shadow" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
          <div
            className="modal-header d-flex justify-content-between align-items-center"
            style={{ padding: "12px 20px", borderBottom: "1px solid #eee", background: "#f8f9fa" }}
          >
            <h5 className="modal-title fw-bold m-0" style={{ fontSize: "1rem" }}>
              Renew / Recharge: {product.product_name}&nbsp;
              <span className="badge bg-light text-dark border">
                Cycle #{nextCycleNumber}
              </span>
            </h5>
            <button
              type="button"
              className="btn btn-default btn-xs"
              onClick={onClose}
              style={{ padding: "2px 8px" }}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", overflow: "hidden" }}>
            <div className="modal-body" style={{ overflowY: "auto", maxHeight: "calc(90vh - 120px)", padding: "20px" }}>
              <div className="alert alert-info py-2 px-3 mb-3 small">
                {product.type === "subscription"
                  ? "Record a new subscription cycle for this product."
                  : "Recharge credits. Credits will be added to the remaining balance."}
              </div>

              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label fw-bold small">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>

                {product.type === "subscription" ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">
                        New Expiry Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">
                        Alert Days Before Expiry
                      </label>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={reminderDays}
                        onChange={(e) => setReminderDays(e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">
                        Total Credits Added <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm"
                        value={totalCredits}
                        onChange={(e) => setTotalCredits(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">
                        Low Balance Alert Threshold
                      </label>
                      <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm"
                        value={lowBalanceAlertAt}
                        onChange={(e) => setLowBalanceAlertAt(e.target.value)}
                      />
                    </div>
                  </>
                )}

                <div className="col-md-6">
                  <label className="form-label fw-bold small">
                    Amount Paid (₹) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control form-control-sm"
                    placeholder="0.00"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-bold small">Notes / Remarks</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    placeholder="e.g. Price increased by 10% this cycle..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                {loading ? "Recording..." : `Confirm Cycle #${nextCycleNumber}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RenewProductModal;
